import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';

interface VideoItem {
  id: number;
  title: string;
  description?: string;
  video_url: string | null;
  poster_url: string | null;
  status: 'active' | 'closed';
  viewer_count: number;
  created_at: string;
}

function getSessionId(): string {
  let sid = sessionStorage.getItem('_video_sid');
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem('_video_sid', sid);
  }
  return sid;
}

function getBrowserInfo(): Record<string, string> {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const tzCountryMap: Record<string, string> = {
      'Asia/Shanghai': 'CN', 'Asia/Chongqing': 'CN', 'Asia/Harbin': 'CN', 'Asia/Urumqi': 'CN',
      'Asia/Hong_Kong': 'HK', 'Asia/Macau': 'MO', 'Asia/Taipei': 'TW',
    };
    const country = tzCountryMap[tz] || '';
    return {
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language || '',
      timezone: tz,
      country,
    };
  } catch {
    return {};
  }
}

function trackEvent(videoId: number, eventType: string, eventData?: Record<string, any>) {
  api
    .post('/video-streams/track', {
      video_id: videoId,
      session_id: getSessionId(),
      event_type: eventType,
      event_data: eventData ?? null,
      browser_info: getBrowserInfo(),
    })
    .catch(() => { });
}

export default function MeetingBroadcast() {
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [activeVideo, setActiveVideo] = useState<VideoItem | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [viewerCount, setViewerCount] = useState(0);
  const viewerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    api
      .get('/video-streams')
      .then(({ data }) => {
        setVideos(Array.isArray(data) ? data : []);
      })
      .catch(() => setVideos([]))
      .finally(() => setLoading(false));
  }, []);

  const startDurationTracking = useCallback(
    (videoId: number) => {
      if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = setInterval(() => {
        if (videoRef.current && !videoRef.current.paused) {
          trackEvent(videoId, 'duration', {
            current_time: Math.floor(videoRef.current.currentTime),
            total_duration: Math.floor(videoRef.current.duration || 0),
          });
        }
      }, 10000);
    },
    []
  );

  const stopDurationTracking = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  const openVideo = (video: VideoItem) => {
    if (video.status === 'closed') return;
    setActiveVideo(video);
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
    trackEvent(video.id, 'view');

    setViewerCount(video.viewer_count || 0);
    api.post(`/video-streams/${video.id}/view`)
      .then(({ data }) => setViewerCount(data.viewer_count))
      .catch(() => { });

    if (viewerIntervalRef.current) clearInterval(viewerIntervalRef.current);
    viewerIntervalRef.current = setInterval(() => {
      api.post(`/video-streams/${video.id}/view`)
        .then(({ data }) => setViewerCount(data.viewer_count))
        .catch(() => { });
    }, 15000);
  };

  const closeVideo = () => {
    stopDurationTracking();
    if (viewerIntervalRef.current) {
      clearInterval(viewerIntervalRef.current);
      viewerIntervalRef.current = null;
    }
    setActiveVideo(null);
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (!videoRef.current || !activeVideo) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
      trackEvent(activeVideo.id, 'play', { current_time: Math.floor(videoRef.current.currentTime) });
      startDurationTracking(activeVideo.id);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
      trackEvent(activeVideo.id, 'pause', { current_time: Math.floor(videoRef.current.currentTime) });
      stopDurationTracking();
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const ct = videoRef.current.currentTime;
    const dur = videoRef.current.duration || 1;
    setProgress((ct / dur) * 100);
    setCurrentTime(ct);
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const handleEnded = () => {
    if (activeVideo) {
      trackEvent(activeVideo.id, 'complete', { total_duration: Math.floor(duration) });
    }
    stopDurationTracking();
    setIsPlaying(false);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = pct * (videoRef.current.duration || 0);
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if ((videoRef.current as any).webkitEnterFullscreen) {
        (videoRef.current as any).webkitEnterFullscreen();
      }
    }
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    return () => {
      stopDurationTracking();
      if (viewerIntervalRef.current) clearInterval(viewerIntervalRef.current);
    };
  }, [stopDurationTracking]);

  const featuredVideo = videos[activeSlide] || videos[0];

  const keyPoints = [
    { num: '01', title: '会议总结', desc: '全面回顾前一阶段工作，肯定成绩，指出不足。', icon: '/images/meeting_zongjie.png' },
    { num: '02', title: '形势分析', desc: '深入分析当前面临的形势与挑战，明确发展机遇。', icon: '/images/meeting_fenxi.png' },
    { num: '03', title: '工作部署', desc: '围绕重点任务，细化工作措施，强化责任落实。', icon: '/images/meeting_bushu.png' },
    { num: '04', title: '目标要求', desc: '明确工作目标，凝聚力量，确保各项任务圆满完成。', icon: '/images/meeting_yaoqiu.png' },
  ];

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#f5f5f5] pb-5">
      <AuthGuard />

      {/* Header bar */}
      <div className="fixed top-0 z-30 flex items-center h-11 px-3">
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 text-black"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
      </div>

      {/* Hero banner */}
      <div className="relative">
        <img src="/images/meeting_shang.png" alt="" className="w-full block" />
      </div>

      {/* White content area */}
      <div className="relative -mt-[230px] z-10 rounded-t-2xl px-4 pt-5 space-y-4 pb-4">

        {/* Section: 最新会议播报 */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Section header */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <div className="flex items-center gap-2">
              <img src="/images/meeting_zuixn.png" alt="" className="w-5 h-5" />
              <span className="text-base font-bold text-gray-900">最新会议播报</span>
            </div>
            <span className="text-xs text-gray-400">查看更多 &gt;</span>
          </div>

          {/* Featured meeting card */}
          {loading && (
            <div className="px-4 pb-4 space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="rounded-xl bg-gray-100 animate-pulse h-[140px]" />
              ))}
            </div>
          )}

          {!loading && featuredVideo && (
            <div
              className="px-4 pb-3 cursor-pointer active:scale-[0.98] transition-transform"
              onClick={() => openVideo(featuredVideo)}
            >
              <div className="flex gap-3">
                {/* Left: poster */}
                <div className="w-[42%] flex-shrink-0 relative rounded-lg overflow-hidden">
                  {featuredVideo.poster_url ? (
                    <img
                      src={featuredVideo.poster_url}
                      alt=""
                      className={`w-full aspect-[4/3] object-cover ${featuredVideo.status === 'closed' ? 'grayscale' : ''}`}
                    />
                  ) : (
                    <div className="w-full aspect-[4/3] bg-red-50 flex items-center justify-center">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c4161c" strokeWidth="1.5"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                    </div>
                  )}
                  {/* Play button overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="white" className="ml-0.5"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                    </div>
                  </div>
                  {/* Red label at bottom */}
                  <div className="absolute bottom-0 left-0">
                    <span className="inline-block bg-[#c4161c] text-white text-[10px] px-2 py-0.5 rounded-tr-lg rounded-bl-lg font-medium">工作会议</span>
                  </div>
                </div>

                {/* Right: info */}
                <div className="flex-1 flex flex-col justify-between py-0.5">
                  <div>
                    <div className="mb-1.5">
                      <img src="/images/meeting_yi.png" alt="重要" className="h-4" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900 leading-snug mb-1 line-clamp-2">{featuredVideo.title}</h3>
                    {featuredVideo.description && (
                      <p className="text-xs text-gray-400 leading-relaxed line-clamp-2 mb-2">{featuredVideo.description}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <img src="/images/meeting_shijian.png" alt="" className="w-3.5 h-3.5" />
                      <span>{formatDate(featuredVideo.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <img src="/images/meeting_didian.png" alt="" className="w-3.5 h-3.5" />
                      <span>总部大会议室</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && videos.length === 0 && (
            <div className="px-4 pb-4">
              <div className="rounded-xl bg-gray-50 p-6 text-center text-sm text-gray-400">暂无会议播报</div>
            </div>
          )}

          {/* Pagination dots */}
          {!loading && videos.length > 0 && (
            <div className="flex items-center justify-center gap-1.5 pb-4">
              {videos.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveSlide(idx)}
                  className={`rounded-full transition-all ${activeSlide === idx ? 'w-5 h-2 bg-[#c4161c]' : 'w-2 h-2 bg-gray-300'}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Section: 会议要点速览 */}
        <div className="bg-white rounded-xl shadow-sm px-4 py-4">
          <div className="flex items-center gap-2 mb-4">
            <img src="/images/meeting_sulan.png" alt="" className="w-5 h-5" />
            <span className="text-base font-bold text-gray-900">会议要点速览</span>
          </div>
          <div className="space-y-3">
            {keyPoints.map((item) => (
              <div key={item.num} className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-3">
                {/* Numbered circle */}
                <div className="w-7 h-7 rounded-full bg-[#f93b33] flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-bold">{item.num}</span>
                </div>
                {/* Text */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-xs font-bold text-gray-900 mb-0.5">{item.title}</h4>
                  <p className="text-[11px] text-gray-400 leading-relaxed line-clamp-1">{item.desc}</p>
                </div>
                {/* Icon */}
                <img src={item.icon} alt="" className="w-8 h-8 flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>

        {/* Section: 会议精神 */}
        <div className="relative overflow-hidden">
          <img src="/images/meeting_jingshen.png" alt="" className="w-full" />
        </div>
      </div>

      {/* Video Player Modal */}
      {activeVideo && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          {/* Modal Header */}
          <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/80 to-transparent p-4">
            <div className="flex items-center justify-between max-w-md mx-auto">
              <button
                onClick={closeVideo}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 backdrop-blur-md text-white active:bg-white/20 transition"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
              <h2 className="flex-1 text-center text-white font-semibold text-sm truncate px-3">
                {activeVideo.title}
              </h2>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-600/90 backdrop-blur-sm rounded-full shadow-lg shadow-red-500/30">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  <span className="text-white text-xs font-bold tracking-wide">回放</span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-black/40 backdrop-blur-sm rounded-full">
                  <span className="text-orange-400 text-xs">🔥</span>
                  <span className="text-white text-xs font-medium">{viewerCount.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Video */}
          <div className="flex-1 flex items-center justify-center" onClick={togglePlay}>
            <video
              ref={videoRef}
              src={activeVideo.video_url || ''}
              poster={activeVideo.poster_url || undefined}
              className="w-full h-full object-contain"
              playsInline
              muted={isMuted}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleEnded}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />

            {!isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/10 animate-pulse">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="white" className="ml-1.5"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                </div>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/80 to-transparent pb-safe">
            <div className="max-w-md mx-auto px-4 pb-4 pt-10">
              {/* Progress Bar */}
              <div
                className="w-full h-1.5 bg-white/10 rounded-full cursor-pointer mb-3 group/bar"
                onClick={handleProgressClick}
              >
                <div
                  className="h-full bg-gradient-to-r from-red-500 to-orange-500 rounded-full relative transition-all"
                  style={{ width: `${progress}%` }}
                >
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white shadow-lg shadow-red-500/30 opacity-0 group-hover/bar:opacity-100 transition" />
                </div>
              </div>

              {/* Control Buttons */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button onClick={togglePlay} className="text-white p-2">
                    {isPlaying ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                    )}
                  </button>
                  <button onClick={toggleMute} className="text-white/70 p-2">
                    {isMuted ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" /></svg>
                    )}
                  </button>
                  <span className="text-white/50 text-xs font-mono">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>
                <button onClick={handleFullscreen} className="text-white/70 p-2">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
